import React, { useState, useEffect, useRef, useCallback } from 'react'
import styles from './SpecialModeView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import { celebrate, popScale, shake } from './juice'

import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import TableChartIcon from '@mui/icons-material/TableChart'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

// ── German adjective-ending model ───────────────────────────────────────────
// Everything here is deterministic: given (pattern, case, gender/number) the
// ending is fixed, so items are built locally and are ALWAYS correct — no model,
// no network. This is the whole point of the mode: remove the guesswork.

export const DEU_CASES = [
  { key: 'nom', label: 'Nominative', q: 'Wer? Was?', role: 'the subject (the doer)' },
  { key: 'acc', label: 'Accusative', q: 'Wen? Was?', role: 'the direct object' },
  { key: 'dat', label: 'Dative', q: 'Wem?', role: 'the indirect object · after mit, bei, von, zu…' },
  { key: 'gen', label: 'Genitive', q: 'Wessen?', role: 'possession · after wegen, trotz, während…' },
]
const CASE_BY_KEY = Object.fromEntries(DEU_CASES.map(c => [c.key, c]))

const GN = [
  { key: 'm', label: 'Masculine', short: 'm.' },
  { key: 'f', label: 'Feminine', short: 'f.' },
  { key: 'n', label: 'Neuter', short: 'n.' },
  { key: 'pl', label: 'Plural', short: 'pl.' },
]
const GN_LABEL = { m: 'masculine', f: 'feminine', n: 'neuter', pl: 'plural' }

export const DEU_PATTERNS = [
  {
    key: 'weak', name: 'Weak', trigger: 'after der / die / das (a definite article)',
    also: 'dieser, jener, jeder, welcher, alle, beide…',
    why: 'The article already shows the case and gender, so the adjective only needs a light ending — always -e or -en.',
  },
  {
    key: 'mixed', name: 'Mixed', trigger: 'after ein / kein / a possessive',
    also: 'mein, dein, sein, ihr, unser, euer…',
    why: 'Where ein has no ending (nom. masc.; nom./acc. neut.) the adjective steps in with a strong ending (-er, -es). Everywhere else it is weak (-e / -en).',
  },
  {
    key: 'strong', name: 'Strong', trigger: 'no article at all',
    also: 'or after etwas, viel, wenig, numbers…',
    why: 'Nothing else marks the case, so the adjective carries the full signal — its endings copy der / das / die.',
  },
]
const PATTERN_BY_KEY = Object.fromEntries(DEU_PATTERNS.map(p => [p.key, p]))

// The three ending tables (rows = case, cols = m/f/n/pl).
const ENDINGS = {
  weak: {
    nom: { m: 'e', f: 'e', n: 'e', pl: 'en' }, acc: { m: 'en', f: 'e', n: 'e', pl: 'en' },
    dat: { m: 'en', f: 'en', n: 'en', pl: 'en' }, gen: { m: 'en', f: 'en', n: 'en', pl: 'en' },
  },
  mixed: {
    nom: { m: 'er', f: 'e', n: 'es', pl: 'en' }, acc: { m: 'en', f: 'e', n: 'es', pl: 'en' },
    dat: { m: 'en', f: 'en', n: 'en', pl: 'en' }, gen: { m: 'en', f: 'en', n: 'en', pl: 'en' },
  },
  strong: {
    nom: { m: 'er', f: 'e', n: 'es', pl: 'e' }, acc: { m: 'en', f: 'e', n: 'es', pl: 'e' },
    dat: { m: 'em', f: 'er', n: 'em', pl: 'en' }, gen: { m: 'en', f: 'er', n: 'en', pl: 'er' },
  },
}
const ENDING_CHOICES = ['e', 'en', 'er', 'es', 'em']

// Determiner declension so the phrase the learner sees is real German.
const DEF = { // definite article → triggers WEAK
  nom: { m: 'der', f: 'die', n: 'das', pl: 'die' }, acc: { m: 'den', f: 'die', n: 'das', pl: 'die' },
  dat: { m: 'dem', f: 'der', n: 'dem', pl: 'den' }, gen: { m: 'des', f: 'der', n: 'des', pl: 'der' },
}
const EIN = { // ein-word endings → triggers MIXED
  nom: { m: '', f: 'e', n: '', pl: 'e' }, acc: { m: 'en', f: 'e', n: '', pl: 'e' },
  dat: { m: 'em', f: 'er', n: 'em', pl: 'en' }, gen: { m: 'es', f: 'er', n: 'es', pl: 'er' },
}
const DET_EN = { der: 'the', ein: 'a', kein: 'no', mein: 'my', dein: 'your', sein: 'his', unser: 'our' }

// Nouns: mass nouns (no plural) also give clean article-less STRONG singulars.
// `gs` = genitive singular (masc./neut. take -s/-es; feminine is unchanged so it
// is omitted). Plurals add dative -n at build time.
const NOUNS = [
  { de: 'Mann', g: 'm', pl: 'Männer', gs: 'Mannes', en: 'man', enPl: 'men' },
  { de: 'Hund', g: 'm', pl: 'Hunde', gs: 'Hundes', en: 'dog', enPl: 'dogs' },
  { de: 'Tisch', g: 'm', pl: 'Tische', gs: 'Tisches', en: 'table', enPl: 'tables' },
  { de: 'Film', g: 'm', pl: 'Filme', gs: 'Films', en: 'film', enPl: 'films' },
  { de: 'Freund', g: 'm', pl: 'Freunde', gs: 'Freundes', en: 'friend', enPl: 'friends' },
  { de: 'Baum', g: 'm', pl: 'Bäume', gs: 'Baumes', en: 'tree', enPl: 'trees' },
  { de: 'Wein', g: 'm', pl: 'Weine', gs: 'Weines', en: 'wine', enPl: 'wines', mass: true },
  { de: 'Frau', g: 'f', pl: 'Frauen', en: 'woman', enPl: 'women' },
  { de: 'Katze', g: 'f', pl: 'Katzen', en: 'cat', enPl: 'cats' },
  { de: 'Blume', g: 'f', pl: 'Blumen', en: 'flower', enPl: 'flowers' },
  { de: 'Stadt', g: 'f', pl: 'Städte', en: 'city', enPl: 'cities' },
  { de: 'Idee', g: 'f', pl: 'Ideen', en: 'idea', enPl: 'ideas' },
  { de: 'Lampe', g: 'f', pl: 'Lampen', en: 'lamp', enPl: 'lamps' },
  { de: 'Milch', g: 'f', pl: null, en: 'milk', mass: true },
  { de: 'Kind', g: 'n', pl: 'Kinder', gs: 'Kindes', en: 'child', enPl: 'children' },
  { de: 'Haus', g: 'n', pl: 'Häuser', gs: 'Hauses', en: 'house', enPl: 'houses' },
  { de: 'Auto', g: 'n', pl: 'Autos', gs: 'Autos', en: 'car', enPl: 'cars' },
  { de: 'Buch', g: 'n', pl: 'Bücher', gs: 'Buches', en: 'book', enPl: 'books' },
  { de: 'Fenster', g: 'n', pl: 'Fenster', gs: 'Fensters', en: 'window', enPl: 'windows' },
  { de: 'Hotel', g: 'n', pl: 'Hotels', gs: 'Hotels', en: 'hotel', enPl: 'hotels' },
  { de: 'Wasser', g: 'n', pl: null, gs: 'Wassers', en: 'water', mass: true },
]
// Dative plural adds -n unless the plural already ends in -n or -s.
const datPlural = (w) => (/[ns]$/.test(w) ? w : w + 'n')
// Regular adjectives only (no stem-changers like teuer/hoch/dunkel, and none
// ending in -e, so stem + ending is always clean).
const ADJS = [
  { de: 'gut', en: 'good' }, { de: 'klein', en: 'small' }, { de: 'groß', en: 'big' }, { de: 'alt', en: 'old' },
  { de: 'neu', en: 'new' }, { de: 'jung', en: 'young' }, { de: 'schön', en: 'beautiful' }, { de: 'rot', en: 'red' },
  { de: 'blau', en: 'blue' }, { de: 'grün', en: 'green' }, { de: 'gelb', en: 'yellow' }, { de: 'kalt', en: 'cold' },
  { de: 'warm', en: 'warm' }, { de: 'laut', en: 'loud' }, { de: 'stark', en: 'strong' }, { de: 'schnell', en: 'fast' },
  { de: 'langsam', en: 'slow' }, { de: 'lang', en: 'long' }, { de: 'kurz', en: 'short' }, { de: 'reich', en: 'rich' },
  { de: 'nett', en: 'nice' }, { de: 'klug', en: 'clever' }, { de: 'frisch', en: 'fresh' }, { de: 'süß', en: 'sweet' },
  { de: 'hart', en: 'hard' }, { de: 'breit', en: 'wide' }, { de: 'schwer', en: 'heavy' }, { de: 'leicht', en: 'light' },
  { de: 'hell', en: 'bright' }, { de: 'wild', en: 'wild' },
]

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)]
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// Case frames that make the phrase carry its case naturally, with an English gloss.
const FRAME = {
  nom: { pre: '', post: (pl) => (pl ? ' sind toll.' : ' ist toll.'), en: (p, pl) => `${cap(p)} ${pl ? 'are' : 'is'} great.` },
  acc: { pre: 'Ich mag ', post: () => '.', en: (p) => `I like ${p}.` },
  dat: { pre: 'Ich komme mit ', post: () => '.', en: (p) => `I'm coming with ${p}.` },
  gen: { pre: 'Das ist wegen ', post: () => '.', en: (p) => `That's because of ${p}.` },
}

function detFor(pattern, cs, gn) {
  if (pattern === 'strong') return { de: '', en: '' }
  if (pattern === 'weak') return { de: DEF[cs][gn], en: 'the' }
  // mixed — ein has no plural, so plurals use kein / a possessive
  const base = gn === 'pl' ? pick(['kein', 'mein', 'dein', 'sein', 'unser']) : pick(['ein', 'kein', 'mein', 'dein', 'sein'])
  return { de: base + EIN[cs][gn], en: DET_EN[base] }
}

function pickNoun(pattern, cs, gn) {
  if (gn === 'pl') return pick(NOUNS.filter(n => n.pl))
  // A bare STRONG singular reads best with a mass noun (guter Wein, gutem Wein…),
  // never an odd article-less count noun ("guter Hund"). We have a mass noun for
  // each gender, so the requested gender is always honoured.
  if (pattern === 'strong') {
    const mass = NOUNS.filter(n => n.mass && n.g === gn)
    if (mass.length) return pick(mass)
  }
  return pick(NOUNS.filter(n => n.g === gn))
}

// The noun form the case & number require.
function nounForm(noun, cs, gn) {
  if (gn === 'pl') return cs === 'dat' ? datPlural(noun.pl) : noun.pl
  if (cs === 'gen' && (noun.g === 'm' || noun.g === 'n')) return noun.gs || noun.de
  return noun.de
}

function buildItem(pattern, cs, gn) {
  const noun = pickNoun(pattern, cs, gn)
  const adj = pick(ADJS)
  const det = detFor(pattern, cs, gn)
  const ending = ENDINGS[pattern][cs][gn]
  const nounDe = nounForm(noun, cs, gn)
  const nounEn = gn === 'pl' ? noun.enPl : noun.en
  const frame = FRAME[cs]

  // Leading text (grey) = frame opener + determiner + adjective STEM; the ending
  // is the blank; then noun + closer. Capitalise the first visible word when the
  // phrase starts the sentence.
  let leading = (det.de ? det.de + ' ' : '') + adj.de
  if (frame.pre === '') leading = cap(leading)
  const prefix = frame.pre + leading
  const suffix = ' ' + nounDe + frame.post(gn === 'pl')

  const pEn = `${det.en ? det.en + ' ' : ''}${adj.en} ${nounEn}`
  const english = frame.en(pEn, gn === 'pl')
  const sentence = prefix + ending + suffix

  return { pattern, cs, gn, det: det.de, adjStem: adj.de, adjEn: adj.en, nounDe, nounEn, ending, prefix, suffix, sentence, english }
}

function buildSet(count, patternFocus, numberMode) {
  const patterns = patternFocus === 'all' ? ['weak', 'mixed', 'strong'] : [patternFocus]
  const numbers = numberMode === 'sg' ? ['m', 'f', 'n'] : numberMode === 'pl' ? ['pl'] : ['m', 'f', 'n', 'pl']
  const out = []
  let guard = 0
  const seen = new Set()
  while (out.length < count && guard < count * 40) {
    guard++
    const it = buildItem(pick(patterns), pick(DEU_CASES).key, pick(numbers))
    const sig = it.sentence
    if (seen.has(sig)) continue          // avoid a duplicate sentence back-to-back
    seen.add(sig)
    out.push(it)
  }
  return out
}

export default function GermanAdjectiveView({ count = 10, patternFocus = 'all', numberMode = 'mixed', voiceSpeed, onBack, onClose }) {
  const [items, setItems] = useState(() => buildSet(count, patternFocus, numberMode))
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [stage, setStage] = useState('pattern') // pattern | ending | done
  const [patChoice, setPatChoice] = useState(null)
  const [patResult, setPatResult] = useState(null) // right | wrong | revealed
  const [patAttempts, setPatAttempts] = useState(0)
  const [endChoice, setEndChoice] = useState(null)
  const [endResult, setEndResult] = useState(null)
  const [endAttempts, setEndAttempts] = useState(0)
  const [endRevealed, setEndRevealed] = useState(false)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  const [showRef, setShowRef] = useState(false)

  const item = items[idx]
  const advanceTimerRef = useRef(null)
  const cardRef = useRef(null)
  const comboRef = useRef(null)
  const patRowRef = useRef(null)
  const endRowRef = useRef(null)
  const completedRef = useRef(false)

  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])

  // Reset per item.
  useEffect(() => {
    setStage('pattern'); setPatChoice(null); setPatResult(null); setPatAttempts(0)
    setEndChoice(null); setEndResult(null); setEndAttempts(0); setEndRevealed(false)
    completedRef.current = false
  }, [idx])

  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => {
      const next = prev + 1
      if (next >= items.length) {
        setItems(buildSet(count, patternFocus, numberMode)); setSetNum(n => n + 1); return 0
      }
      return next
    })
  }, [items.length, count, patternFocus, numberMode])

  const completeItem = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    setStage('done')
    const el = cardRef.current
    if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 28, spread: 170 }) }
    setCombo(c => { const nc = c + 1; setBest(b => Math.max(b, nc)); if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 64, spread: 260 }); return nc })
    advanceTimerRef.current = setTimeout(advance, 950)
  }, [advance])

  const pickPattern = (key) => {
    if (!item || patResult === 'right' || patResult === 'revealed') return
    setPatChoice(key)
    if (key === item.pattern) {
      setPatResult('right')
      setTimeout(() => setStage('ending'), 650)
    } else {
      const n = patAttempts + 1
      setPatAttempts(n)
      setPatResult('wrong')
      shake(patRowRef.current)
      setCombo(0)
      if (n >= 2) { setPatChoice(item.pattern); setPatResult('revealed') }
    }
  }
  const proceedToEnding = () => setStage('ending')

  const pickEnding = (val) => {
    if (!item || completedRef.current || endRevealed) return
    setEndChoice(val)
    if (val === item.ending) { setEndResult('right'); completeItem(); return }
    const n = endAttempts + 1
    setEndAttempts(n)
    setEndResult('wrong')
    shake(endRowRef.current)
    setCombo(0)
    if (n >= 2) setEndRevealed(true)
  }

  const skip = () => { setCombo(0); advance() }
  const hear = () => { if (item) speak(item.sentence, voices.deu || voices.eng, voiceSpeed) }

  if (!item) return null
  const patObj = PATTERN_BY_KEY[item.pattern]
  const caseObj = CASE_BY_KEY[item.cs]
  const patternKnown = stage !== 'pattern' || patResult === 'right' || patResult === 'revealed'
  const slotText = stage === 'done'
    ? item.ending
    : (endRevealed ? item.ending : (patternKnown ? '-…' : '···'))

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · Adjective endings · German</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '✦'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx} ref={cardRef}>
          <div className={styles.sentenceLine}>
            <span className={styles.ctx}>{item.prefix}</span>
            <span className={styles.slot}>{slotText}</span>
            <span className={styles.ctx}>{item.suffix}</span>
          </div>
          <div className={styles.english}>{item.english}</div>

          {/* the case + gender are given; the learner supplies pattern + ending */}
          <div style={chipInfoRow}>
            <span style={infoChip}>{caseObj?.label}</span>
            <span style={infoChip}>{GN_LABEL[item.gn]}</span>
            {patternKnown && <span style={{ ...infoChip, ...infoChipStrong }}>{patObj?.name}</span>}
          </div>

          {/* stage 1: which pattern? */}
          {stage === 'pattern' && (
            <>
              <div className={styles.stageLabel}>Which pattern applies? Look at what comes <strong>before</strong> the adjective.</div>
              <div className={styles.caseGrid} ref={patRowRef} style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                {DEU_PATTERNS.map(p => {
                  const chosen = patChoice === p.key
                  const isAnswer = item.pattern === p.key
                  const cls = [
                    styles.caseBtn,
                    patResult && isAnswer && (patResult === 'right' || patResult === 'revealed') ? styles.caseRight : '',
                    chosen && patResult === 'wrong' && !isAnswer ? styles.caseWrong : '',
                  ].join(' ')
                  return (
                    <div key={p.key} className={styles.caseCell}>
                      <button className={cls} onClick={() => pickPattern(p.key)}>
                        <span className={styles.caseName}>{p.name}</span>
                        <span className={styles.caseQ}>{p.trigger}</span>
                      </button>
                      <div className={styles.caseInfo}>
                        <span className={styles.caseInfoHead}>{p.name} declension</span>
                        <span className={styles.caseInfoQ}>{p.trigger}</span>
                        <span className={styles.caseInfoLine}>Also: {p.also}</span>
                        <span className={styles.caseInfoLine}>{p.why}</span>
                      </div>
                    </div>
                  )
                })}
              </div>

              {patResult === 'wrong' && patAttempts < 2 && (
                <div className={styles.nudge}>Not quite — is there an article? Is it <em>der/die/das</em>, an <em>ein</em>-word, or nothing?</div>
              )}
              {patResult === 'revealed' && (
                <>
                  <div className={styles.whyBox}>
                    It's <strong>{patObj?.name}</strong> — {patObj?.trigger}. {patObj?.why}
                  </div>
                  <button className={styles.continueBtn} onClick={proceedToEnding}>Continue →</button>
                </>
              )}
            </>
          )}

          {/* stage 2: which ending? */}
          {(stage === 'ending' || stage === 'done') && (
            <>
              <div className={styles.whyBox}>
                <strong>{patObj?.name}</strong> ✓ — {patObj?.trigger}.
              </div>
              <div className={styles.stageLabel}>
                Now the ending: <strong>{cap(item.adjStem)}</strong> in the {caseObj?.label}, {GN_LABEL[item.gn]}.
              </div>
              <div className={styles.chipRow} ref={endRowRef} style={{ justifyContent: 'center', gap: '0.6rem' }}>
                {ENDING_CHOICES.map(e => {
                  const chosen = endChoice === e
                  const isAnswer = item.ending === e
                  let bg = '#141418', border = '#2a2a33', color = '#e8e8ec'
                  if (stage === 'done' || endRevealed) {
                    if (isAnswer) { bg = 'rgba(52,211,153,0.16)'; border = '#34d399'; color = '#a7f3d0' }
                  } else if (chosen && endResult === 'wrong') { bg = 'rgba(248,113,113,0.16)'; border = '#f87171'; color = '#fca5a5' }
                  return (
                    <button key={e} onClick={() => pickEnding(e)} disabled={stage === 'done' || endRevealed}
                      style={{ ...endBtn, background: bg, borderColor: border, color }}>-{e}</button>
                  )
                })}
              </div>
              {endResult === 'wrong' && !endRevealed && (
                <div className={styles.nudge}>Try again — or open the tables to find the exact cell.</div>
              )}
              {endRevealed && stage !== 'done' && (
                <>
                  <div className={styles.revealNote}>
                    Answer: <strong>{item.adjStem}{item.ending}</strong> &nbsp;(the ending is <strong>-{item.ending}</strong>)
                  </div>
                  <button className={styles.continueBtn} onClick={advance}>Continue →</button>
                </>
              )}
            </>
          )}

          {stage === 'done' && <div className={styles.doneMsg}>{patAttempts === 0 && endAttempts === 0 ? '★ Perfect!' : '✓ Nice'}</div>}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={hear} title="Hear the full sentence"><VolumeUpIcon /></button>
          <button className={styles.sideBtn} onClick={() => setShowRef(true)} title="The three ending tables — guides you to the right cell"><TableChartIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
          {onBack && <button className={styles.sideBtn} onClick={onBack} title="Back to setup"><ArrowBackIcon /></button>}
        </div>
        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
        </div>
      </div>

      {showRef && <DeuRefBoard item={item} onClose={() => setShowRef(false)} />}
    </div>
  )
}

// ── the three ending tables, with a guided highlight ────────────────────────
// phase 0: all three tables · phase 1: spotlight the item's pattern table ·
// phase 2: spotlight the exact case-row × gender-col cell and reveal the ending.
function DeuRefBoard({ item, onClose }) {
  const [phase, setPhase] = useState(0)
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1600)
    const t2 = setTimeout(() => setPhase(2), 3200)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  const table = (p) => {
    const isActive = p.key === item.pattern
    const dim = phase >= 1 && !isActive
    return (
      <div key={p.key} style={{ ...refTableWrap, opacity: dim ? 0.32 : 1, borderColor: phase >= 1 && isActive ? '#34d399' : '#2a2a33' }}>
        <div style={refTableTitle}>
          {p.name}<span style={{ color: '#6ee7b7', fontWeight: 500 }}> · {p.trigger.replace(/ \(.*\)/, '')}</span>
        </div>
        <div style={refGrid}>
          <div style={refHeadCell}></div>
          {GN.map(g => <div key={g.key} style={refHeadCell}>{g.short}</div>)}
          {DEU_CASES.map(c => (
            <React.Fragment key={c.key}>
              <div style={refCaseCell}>{c.label.slice(0, 3)}.</div>
              {GN.map(g => {
                const isTarget = phase >= 2 && isActive && c.key === item.cs && g.key === item.gn
                return (
                  <div key={g.key} style={{ ...refCell, ...(isTarget ? refTargetCell : null) }}>
                    -{ENDINGS[p.key][c.key][g.key]}
                  </div>
                )
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.refOverlay} onClick={onClose}>
      <div className={styles.refModal} onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.modeLabel}>The three ending tables</div>
        <div className={styles.refStatus}>
          {phase === 0 ? 'Three patterns — first find the one for your determiner…'
            : phase === 1 ? <>Your pattern: <strong>{PATTERN_BY_KEY[item.pattern]?.name}</strong> — now the {CASE_BY_KEY[item.cs]?.label}, {GN_LABEL[item.gn]}…</>
              : <>{item.adjStem} → <strong className={styles.refHi}>{item.adjStem}{item.ending}</strong> &nbsp;(ending <strong>-{item.ending}</strong>)</>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '58vh', overflowY: 'auto', padding: '0.2rem' }}>
          {DEU_PATTERNS.map(table)}
        </div>

        <div className={styles.refFoot}>
          {phase < 2
            ? <button className={styles.checkBtn} onClick={() => setPhase(2)}>Reveal my cell →</button>
            : <button className={styles.checkBtn} onClick={onClose}>Got it</button>}
        </div>
      </div>
    </div>
  )
}

// ── inline styles (kept local so no SCSS edit is needed) ────────────────────
const chipInfoRow = { display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap', margin: '0.2rem 0 0.9rem' }
const infoChip = { fontSize: '0.78rem', padding: '0.2rem 0.6rem', borderRadius: 999, background: '#141418', border: '1px solid #2a2a33', color: '#b7b7c2' }
const infoChipStrong = { background: 'rgba(96,165,250,0.15)', borderColor: '#3b82f6', color: '#bfdbfe' }
const endBtn = { minWidth: 62, fontSize: '1.15rem', fontWeight: 700, padding: '0.55rem 0.7rem', borderRadius: 12, border: '1px solid #2a2a33', cursor: 'pointer', transition: 'transform .1s' }

const refTableWrap = { border: '1px solid #2a2a33', borderRadius: 12, padding: '0.7rem', transition: 'opacity .3s, border-color .3s' }
const refTableTitle = { fontSize: '0.9rem', fontWeight: 700, color: '#e8e8ec', marginBottom: '0.5rem' }
const refGrid = { display: 'grid', gridTemplateColumns: '48px repeat(4, 1fr)', gap: '4px' }
const refHeadCell = { fontSize: '0.74rem', color: '#8a8a95', textAlign: 'center', padding: '0.25rem 0', textTransform: 'uppercase', letterSpacing: '0.04em' }
const refCaseCell = { fontSize: '0.78rem', color: '#8a8a95', display: 'flex', alignItems: 'center', fontWeight: 600 }
const refCell = { fontSize: '0.92rem', color: '#d8d8e0', textAlign: 'center', padding: '0.35rem 0', background: '#141418', borderRadius: 7, border: '1px solid transparent' }
const refTargetCell = { background: 'rgba(52,211,153,0.18)', borderColor: '#34d399', color: '#a7f3d0', fontWeight: 700, transform: 'scale(1.06)' }
